/**
 * useCancelSubscriptionSubmit — le POST qui résilie, et l'accusé qui suit.
 *
 * Les deux dernières étapes postent la MÊME chose sur la même tranche (`CancelFeesScreen.js:138-191`
 * et `CancelPaymentScreen.js:91-104`) : seuls le montant affiché — 0 € quand il n'y a pas de frais,
 * le montant prélevé sinon — et ce que l'accusé porte en plus les distinguent. La garde `awaiting`
 * de `useApiMutation` compte donc doublement : les deux étapes sont montées l'une sur l'autre.
 *
 * `subscription_state.end_date` est la fin d'accès canonique, posée par Cashier au `cancel()` ;
 * l'aperçu n'en est que l'approximation (`CancelFeesScreen.js:96-101`) — d'où les replis.
 */
import { useCallback, useRef } from "react"
import { useNavigation } from "@react-navigation/native"

import { subscriptionService } from "../../../../../../data/services/subscriptionService"
import { type ApiCancelSubscriptionResult } from "../../../../adapters/parent/SubscriptionCancel/subscriptionCancelAdapter"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { CANCEL_ROUTE_GOODBYE, type CancelFlowNavigation, type CancelFlowRouteParams } from "./cancelFlowNavigation"

export type UseCancelSubscriptionSubmitArgs = {
    routeParams: CancelFlowRouteParams
    /** Ce que la carte paie : 0 à l'étape 3, le montant affiché à l'étape 4. */
    dueCents: number
    /** Repli de la fin d'accès, quand la réponse ne la porte pas. */
    endOfMonth: string | null
    /** Repli de l'adresse à qui l'accusé de résiliation part. */
    parentEmail: string | null
    /** Ce que l'étape ajoute à l'accusé, en plus des champs communs. */
    goodbyeExtras?: Record<string, unknown>
    /** L'erreur BRUTE : chaque étape en tire sa propre forme de refus. */
    onError: (error: unknown) => void
}

export function useCancelSubscriptionSubmit(args: UseCancelSubscriptionSubmitArgs): () => void {
    const navigation = useNavigation<CancelFlowNavigation>()
    /** Les arguments au dernier rendu : le geste reste stable alors que l'aperçu bouge. */
    const argsRef = useRef(args)
    argsRef.current = args

    const { start } = useApiMutation({
        service: subscriptionService.cancelSubscription,
        gesture: "subscription.cancel",
        onSuccess: (data) => {
            const current = argsRef.current
            const result = data as ApiCancelSubscriptionResult | undefined
            navigation.replace(CANCEL_ROUTE_GOODBYE, {
                ...current.routeParams,
                outcome: "cancelled",
                paid_cents: current.dueCents,
                end_of_month: result?.subscription_state?.end_date ?? current.endOfMonth ?? null,
                parent_email: result?.parent_email ?? current.parentEmail ?? null,
                ...(current.goodbyeExtras ?? {}),
            })
        },
        onError: (_message, error) => argsRef.current.onError(error),
    })

    return useCallback(() => {
        const current = argsRef.current
        start(
            {},
            {
                motif: current.routeParams.motif,
                motif_free_text: current.routeParams.motif_free_text ?? null,
                outcome: "cancelled",
                final_step: 5,
                fees_displayed_cents: current.dueCents,
            }
        )
    }, [start])
}
